import { createSelector } from '@reduxjs/toolkit'

import type { QueryResultLike } from '@krp/client-runtime'

import { makeQueryViewSelector, readyData } from '../../../shared/model/query'
import type { AqsiSaleExchange, AqsiSaleExchangeList, ExchangeItemVm, ExchangeLineVm, ExchangeTone, SaleExchangeVm } from './types'

/** Виды операции продажи словами; неизвестный вид — как пришёл. */
const KIND: Readonly<Record<string, string>> = {
  Purchase: 'Оплата картой',
  Receipt: 'Чек',
  RefundPayment: 'Возврат на карту',
  RefundReceipt: 'Чек возврата',
  ShiftOpen: 'Открытие смены',
  ShiftClose: 'Закрытие смены',
  DeviceCheck: 'Проверка связи с кассой (состояние ФН)',
}

const COMMAND: Readonly<Record<string, string>> = {
  submit: 'отправка в AQSI',
  cancel: 'просьба снять с терминала',
}

/** Почему строка показана у продажи, если она не сделана по самой продаже. */
function relationTextOf(item: AqsiSaleExchange): string | null {
  switch (item.relation) {
    case 'sale':
      return null
    case 'operation':
      return 'По операции этой продажи у AQSI'
    case 'device':
      return `Команда кассы ${item.deviceId ?? '—'} без привязки к продаже — показана рядом по времени`
    case 'foreign-webhook':
      return 'Уведомление AQSI по операции, которой нет ни у одной продажи программы («не наша»), — показано рядом по времени'
    default:
      return item.relation
  }
}

/** Что прочитано у AQSI — по адресу запроса. */
function readTitleOf(path: string): string {
  if (path.startsWith('v4/Operations/')) return 'Чтение состояния операции у AQSI'
  if (path.startsWith('v4/Slips?')) return 'Чтение списка слипов у AQSI'
  if (path.startsWith('v4/Slips/')) return 'Чтение слипа у AQSI'
  return 'Чтение у AQSI'
}

function titleOf(item: AqsiSaleExchange): string {
  if (item.command === 'webhook') return 'Уведомление от AQSI (вебхук)'
  if (item.command === 'read') return readTitleOf(item.path)
  const kind = item.operationKind === null ? 'Операция' : (KIND[item.operationKind] ?? item.operationKind)
  return `${kind}: ${COMMAND[item.command] ?? item.command}`
}

/** Чек и чек возврата несут кассира (`info.cashierInfo`); у операций с картой кассира в запросе нет. */
const RECEIPT_KINDS: ReadonlySet<string> = new Set(['Receipt', 'RefundReceipt'])

/** Поля о кассире в теле запроса или ответа — эти строки выделяются. */
const CASHIER_FIELD = /"(cashier[A-Za-z]*|positionAndSurname)"\s*:/i

/**
 * Момент с секундами и миллисекундами по часам браузера: запрос и ответ разделяют доли секунды. `timeZone` — для тестов.
 */
export function formatExactInstant(value: string, timeZone?: string): string {
  const time = Date.parse(value)
  if (Number.isNaN(time)) return value
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
    hourCycle: 'h23',
    ...(timeZone !== undefined ? { timeZone } : {}),
  })
    .format(time)
    .replace(',', '')
}

/**
 * JSON с отступами — только расстановкой пробелов и переводов строк между лексемами: значения (числа, строки, порядок полей) остаются
 * ровно такими, как ушли или пришли. Не JSON (страница ошибки, обрезанное тело) — `null`: тогда показывается текст как есть.
 */
export function indentJson(text: string): string | null {
  const trimmed = text.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null
  try {
    JSON.parse(trimmed)
  } catch {
    return null
  }
  let out = ''
  let depth = 0
  let inString = false
  let escaped = false
  const pad = () => '\n' + '  '.repeat(depth)
  for (const ch of trimmed) {
    if (inString) {
      out += ch
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
      continue
    }
    switch (ch) {
      case '"':
        inString = true
        out += ch
        break
      case '{':
      case '[':
        depth++
        out += ch + pad()
        break
      case '}':
      case ']':
        depth--
        out += pad() + ch
        break
      case ',':
        out += ',' + pad()
        break
      case ':':
        out += ': '
        break
      case ' ':
      case '\n':
      case '\r':
      case '\t':
        break
      default:
        out += ch
    }
  }
  return out.replace(/([{[])\n\s*([}\]])/g, '$1$2')
}

function linesOf(text: string): ExchangeLineVm[] {
  return text.split('\n').map((line) => ({ text: line, cashier: CASHIER_FIELD.test(line) }))
}

/** Что о кассире ушло в чеке: все поля `info.cashierInfo` словами; нет блока — «не передан». Не чек — `null`. */
export function cashierTextOf(item: AqsiSaleExchange): string | null {
  if (item.command !== 'submit' || item.operationKind === null || !RECEIPT_KINDS.has(item.operationKind)) return null
  let body: unknown
  try {
    body = JSON.parse(item.requestBody)
  } catch {
    return 'Кассир в запросе: тело запроса не разобрано — смотрите текст запроса'
  }
  const info = typeof body === 'object' && body !== null ? (body as Record<string, unknown>).info : undefined
  const cashier = typeof info === 'object' && info !== null ? (info as Record<string, unknown>).cashierInfo : undefined
  if (typeof cashier !== 'object' || cashier === null) return 'Кассир в запросе не передан (блока cashierInfo нет)'
  const fields = Object.entries(cashier as Record<string, unknown>)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([name, value]) => `${name}: «${typeof value === 'string' ? value : JSON.stringify(value)}»`)
  return fields.length === 0 ? 'Кассир в запросе пустой (cashierInfo без значений)' : `Кассир в запросе — ${fields.join(', ')}`
}

function statusOf(item: AqsiSaleExchange): { text: string; tone: ExchangeTone } {
  const status = item.httpStatus
  if (item.command === 'webhook') return { text: `Уведомление получено, программа ответила AQSI HTTP ${status ?? '—'}`, tone: 'ok' }
  if (status === null) return { text: 'Ответа нет', tone: 'danger' }
  if (status >= 200 && status < 300) return { text: `HTTP ${status} — кабинет принял запрос`, tone: 'ok' }
  if (status >= 500) return { text: `HTTP ${status} — ошибка на стороне кабинета AQSI`, tone: 'danger' }
  if (status === 408) return { text: `HTTP ${status} — кабинет не дождался запроса`, tone: 'danger' }
  return { text: `HTTP ${status} — кабинет отказал`, tone: 'warning' }
}

function cutText(kept: number, total: number | null): string | null {
  return total !== null && total > kept ? `Тело обрезано: сохранено ${kept} из ${total} символов` : null
}

function itemVm(saleId: string, item: AqsiSaleExchange, timeZone?: string): ExchangeItemVm {
  const titleText = titleOf(item)
  const relationText = relationTextOf(item)
  const webhook = item.command === 'webhook'
  const requestTitle = webhook ? 'Что прислал AQSI' : item.method === 'GET' ? 'Что запросили у AQSI' : 'Что отправили в AQSI'
  const responseTitle = webhook ? 'Что ответила программа' : 'Что ответил AQSI'
  const facts = [
    ...(item.externalOperationId !== null ? [`Операция у AQSI: ${item.externalOperationId}`] : []),
    ...(item.operationId !== null ? [`Операция программы: ${item.operationId}`] : []),
    ...(item.deviceId !== null ? [`Касса: ${item.deviceId}`] : []),
    ...(item.mentionedOperationId !== null ? [`AQSI назвал в отказе другую операцию: ${item.mentionedOperationId}`] : []),
    ...(item.note !== null ? [`Что сделала программа: ${item.note}`] : []),
  ]
  const sentText = formatExactInstant(item.sentAt, timeZone)
  const durationText = `${item.durationMs} мс`
  const status = statusOf(item)
  const requestText = item.requestBody === '' ? '(тела нет)' : (indentJson(item.requestBody) ?? item.requestBody)
  const responseText = item.responseBody === null ? null : (indentJson(item.responseBody) ?? item.responseBody)
  const requestCutText = cutText(item.requestBody.length, item.requestLength)
  const responseCutText = item.responseBody === null ? null : cutText(item.responseBody.length, item.responseLength)
  const cashierText = cashierTextOf(item)
  const reportText = [
    `Обмен с AQSI — продажа ${saleId}`,
    titleText,
    ...(relationText !== null ? [relationText] : []),
    `${webhook ? 'Получено' : 'Отправлено'}: ${sentText}; ответ через ${durationText}`,
    `${item.method} ${item.path}`,
    ...facts,
    `Ответ: ${status.text}`,
    ...(item.failure !== null ? [`Причина: ${item.failure}`] : []),
    ...(cashierText !== null ? [cashierText] : []),
    `--- ${requestTitle} (как есть) ---`,
    item.requestBody,
    ...(requestCutText !== null ? [`[${requestCutText}]`] : []),
    `--- ${responseTitle} (как есть) ---`,
    item.responseBody ?? (webhook ? 'тела ответа нет' : 'ответа нет'),
    ...(responseCutText !== null ? [`[${responseCutText}]`] : []),
  ].join('\n')
  return {
    id: item.id,
    titleText,
    relationText,
    facts,
    requestTitle,
    responseTitle,
    sentText,
    durationText,
    statusText: status.text,
    tone: status.tone,
    requestText,
    requestLines: linesOf(requestText),
    cashierText,
    requestCutText,
    responseText,
    responseLines: responseText === null ? [] : linesOf(responseText),
    responseCutText,
    failureText: item.failure,
    reportText,
  }
}

export interface SaleExchangeVmInput {
  resourceId: string
  saleId: string
  exchanges: QueryResultLike<AqsiSaleExchangeList>
  /** Часовой пояс показа — для тестов; в приложении — часы браузера. */
  timeZone?: string
}

export const makeSelectSaleExchangeVm = () => {
  const selectView = makeQueryViewSelector(
    (input: SaleExchangeVmInput) => input.exchanges,
    (data) => data.items.length === 0,
  )
  return createSelector(
    [
      (input: SaleExchangeVmInput) => input.resourceId,
      (input: SaleExchangeVmInput) => input.saleId,
      (input: SaleExchangeVmInput) => input.exchanges.data,
      (input: SaleExchangeVmInput) => input.timeZone,
      selectView,
    ],
    (resourceId, saleId, data, timeZone, query): SaleExchangeVm => {
      const list = readyData(query, data)
      const items = list === undefined ? [] : list.items.map((item) => itemVm(saleId, item, timeZone))
      return {
        resourceId,
        saleId,
        query,
        items,
        incompleteText:
          list !== undefined && !list.complete ? `Показаны последние ${items.length} строк: более ранние запросы этой продажи не поместились.` : null,
        reportText: items.map((item) => item.reportText).join('\n\n'),
      }
    },
  )
}
